import axios from '@/axios';
import Cookies from 'js-cookie';

const API_BASE = `${process.env.VUE_APP_API_BASE_URL}/api/accounts`;

// Helper: decode JWT payload (base64url) to extract fields like rol/inmobiliaria_id
function decodeJwtPayload(token) {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
}

const state = {
  token: Cookies.get('token') || '',
  refreshToken: Cookies.get('refreshToken') || '',
  user: null,
  rol: Cookies.get('userRole') || 'cliente',
  inmobiliariaId: Cookies.get('inmobiliariaId') || null,
};

const mutations = {
  SET_TOKEN(state, token) {
    state.token = token;
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    Cookies.set('token', token, { 
      secure: !isLocal, 
      sameSite: isLocal ? 'lax' : 'none',
      domain: isLocal ? undefined : '.inmob.com.ar',
      path: '/'
    });
  },
  SET_REFRESH_TOKEN(state, refreshToken) {
    state.refreshToken = refreshToken;
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    Cookies.set('refreshToken', refreshToken, { 
      secure: !isLocal, 
      sameSite: isLocal ? 'lax' : 'none',
      domain: isLocal ? undefined : '.inmob.com.ar',
      path: '/'
    });
  },
  SET_USER(state, user) {
    state.user = user;
  },
  SET_ROL(state, rol) {
    state.rol = rol;
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    Cookies.set('userRole', rol, {
      secure: !isLocal,
      sameSite: isLocal ? 'lax' : 'none',
      domain: isLocal ? undefined : '.inmob.com.ar',
      path: '/'
    });
  },
  SET_INMOBILIARIA_ID(state, inmobiliariaId) {
    state.inmobiliariaId = inmobiliariaId;
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (inmobiliariaId) {
      Cookies.set('inmobiliariaId', inmobiliariaId, {
        secure: !isLocal,
        sameSite: isLocal ? 'lax' : 'none',
        domain: isLocal ? undefined : '.inmob.com.ar',
        path: '/'
      });
    } else {
      Cookies.remove('inmobiliariaId', { domain: '.inmob.com.ar', path: '/' });
      Cookies.remove('inmobiliariaId');
    }
  },
  LOGOUT(state) {
    state.token = '';
    state.refreshToken = '';
    state.rol = 'cliente';
    state.inmobiliariaId = null;
    
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const cookieOptions = {
      secure: !isLocal,
      sameSite: isLocal ? 'lax' : 'none',
      domain: isLocal ? undefined : '.inmob.com.ar',
      path: '/'
    };
    
    // Remover con las opciones correctas
    Cookies.remove('token', cookieOptions);
    Cookies.remove('refreshToken', cookieOptions);
    Cookies.remove('userRole', cookieOptions);
    Cookies.remove('inmobiliariaId', cookieOptions);
    
    // Intenta remover también sin dominio (fallback)
    try {
      Cookies.remove('token');
      Cookies.remove('refreshToken');
      Cookies.remove('userRole');
      Cookies.remove('inmobiliariaId');
    } catch (e) {
      // ignorar errores en fallback
    }
    
    console.log('Cookies removed');
  },
};

const actions = {
  async register({ dispatch }, payload) {
    try {
      await axios.post(`${API_BASE}/registro/`, payload);
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async login({ commit, dispatch, state }, payload) {
    try {
      const { data } = await axios.post(`${API_BASE}/token/`, payload);
      commit('SET_TOKEN', data.access);
      commit('SET_REFRESH_TOKEN', data.refresh);
      await dispatch('loadUser');

      // Cargar suscripción para admin/agentes
      const userRole = state.rol || '';
      if (['Admin Inmobiliaria', 'Super Admin', 'Agente'].includes(userRole)) {
        await dispatch('billing/fetchSubscription', null, { root: true });
      }

      // Sincronizar notificaciones
      await dispatch('syncFCMToken');
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  async refreshToken({ commit, state }) {
    try {
      const { data } = await axios.post(`${API_BASE}/token/refresh/`, {
        refresh: state.refreshToken,
      });
      commit('SET_TOKEN', data.access);
    } catch (error) {
      commit('LOGOUT');
    }
  },
  async loadUser({ commit, state }) {
    if (!state.token) {
      console.warn('No token in state, skipping loadUser');
      return;
    }
    
    // Primero: extraer datos del JWT (es la fuente de verdad para rol e inmobiliaria_id)
    const payload = decodeJwtPayload(state.token);
    if (payload) {
      console.log('📋 JWT Payload completo:', JSON.stringify(payload, null, 2));
      
      const tokenRol = payload.rol || 'cliente';
      const tokenInmobId = payload.inmobiliaria_id ?? null;
      
      // Advertencia si falta rol
      if (!payload.rol) {
        console.error('❌ CRÍTICO: JWT no contiene campo "rol". Backend no está agregando rol al token.');
        console.error('Campo "rol" esperado pero no encontrado en payload.');
      }
      
      commit('SET_ROL', tokenRol);
      commit('SET_INMOBILIARIA_ID', tokenInmobId);
      console.log('✅ User role set to:', tokenRol, '| Inmobiliaria ID:', tokenInmobId);
    } else {
      console.error('❌ Could not decode JWT payload');
    }
    
    // Segundo: intentar enriquecer datos desde /profile/ si existe (para datos adicionales del usuario)
    try {
      const { data } = await axios.get(`${API_BASE}/profile/`, {
        withCredentials: true,
      });
      commit('SET_USER', data);
      console.log('✅ User data loaded from profile endpoint');
    } catch (error) {
      const status = error?.response?.status;
      // 404: usuario admin, no tiene perfil de cliente, es normal
      if (status === 404) {
        console.info('ℹ️ Profile endpoint returned 404 (admin user without client profile), using JWT data only');
        return;
      }
      // 401/403: error de autenticación real, cerrar sesión
      if (status === 401 || status === 403) {
        console.error('❌ Authentication error (401/403), logging out');
        commit('LOGOUT');
        return;
      }
      // Otros errores: loguear pero mantener sesión
      console.warn('⚠️ Could not load profile endpoint:', error.message);
    }
  },
  async updateUser({ commit }, payload) {
    try {
      const { data } = await axios.put(`${API_BASE}/profile/`, payload, {
        withCredentials: true,
      });
      commit('SET_USER', data);
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  logout({ commit }) {
    commit('LOGOUT');
  },
  
  async syncFCMToken({ state }) {
    try {
      // Solo sincronizar si el usuario está autenticado y las notificaciones están habilitadas
      if (!state.token) return;
      
      const fcmToken = localStorage.getItem('fcm_token');
      const notificationsEnabled = localStorage.getItem('fcm_notifications_enabled') === 'true';
      
      if (fcmToken && notificationsEnabled) {
        await axios.post(`${API_BASE}/fcm-token/`, 
          { fcm_token: fcmToken },
          { withCredentials: true }
        );
        console.log('Token FCM sincronizado tras login');
      }
    } catch (error) {
      console.error('Error sincronizando token FCM:', error);
    }
  },
};

const getters = {
  isAuthenticated: state => !!state.token,
  getUser: state => state.user,
  getRol: state => state.rol,
  getInmobiliariaId: state => state.inmobiliariaId,
  isAdmin: state => ['Admin Inmobiliaria', 'Super Admin'].includes(state.rol),
  isAgent: state => state.rol === 'Agente',
  isSuperAdmin: state => state.rol === 'Super Admin',
  isAdminInmobiliaria: state => state.rol === 'Admin Inmobiliaria',
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};